// Fixed infinite render loop and UI issues
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, Grid, Paper, Table, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Typography, CircularProgress
} from '@mui/material';
import TrainFilters from './Filters';
import SandboxTrainDetail from 'pages/sandbox/SandboxTrainDetail';
import SearchBar from './SearchBar';
import SandBoxCard from './SandBoxCard';
import { TrainAndSandBox, TrainData } from './types';
import SandboxTrainListTable from 'pages/sandbox/SandboxTrainListTable';
import SandboxHeaderBar from 'pages/sandbox/SandboxHeaderBar';
import { CemitAppOrganizationDependencyProps } from 'types/propTypes/appPropTypes/cemitAppOrganizationDependencyProps';
import { useListAllTrains } from "../sandbox/hooks/useListAllTrains";
import { useTrainSandboxes } from "../sandbox/hooks/useFetchSandBoxIds";
import SandUsageMap from './SandUsageMap';

const MemoizedSandBoxCard = React.memo(SandBoxCard);
const MemoizedSandboxTrainDetail = React.memo(SandboxTrainDetail);

const SandboxTrainListContainer: React.FC<CemitAppOrganizationDependencyProps> = ({ appProps, organizationProps }) => {
  const [selectedTrainSandbox, setSelectedTrainSandbox] = useState<TrainAndSandBox | null>(null);
  const [isMapShown, setIsMapShown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTrainId, setSelectedTrainID] = useState<string | null>(null);
  const [openMap, setOepnMap] = useState<boolean | null>(null);
  const [usageTrainID, setUsageTrainID] = useState<string | null>(null);
  const [trainStatuses, setTrainStatuses] = useState<Map<string, string>>(new Map());
  const [selectedFilters, setSelectedFilters] = useState<any[]>([]);
  const [selectedValues, setSelectedValues] = useState<any>({
    'Train ID': '', 'Status': '', 'Fleet': '', 'Start Location': '', 'End Location': ''
  });

  const { data: locations, error, isLoading: trainListLoading } = useListAllTrains();
  const { trainsDataWithSandBoxIds, refreshSandboxes } = useTrainSandboxes(locations?.locations || []);

  const handleFilterChange = (filter: string, value: string) => {
    setSelectedValues(prev => ({ ...prev, [filter]: value }));
    const existingIndex = selectedFilters.findIndex(f => f.filter === filter);
    if (existingIndex >= 0) {
      setSelectedFilters(prev => prev.map((f, i) => i === existingIndex ? { ...f, value } : f));
    } else {
      setSelectedFilters(prev => [...prev, { filter, value }]);
    }
  };

  const handleChipDelete = (filter: string) => {
    setSelectedFilters(prev => prev.filter(f => f.filter !== filter));
    setSelectedValues(prev => ({ ...prev, [filter]: '' }));
  };

  const handleSearch = (value: string) => setSearchQuery(value);

  const handleUpdateTrainStatus = useMemo(() => {
    return (trainId: string, status: string) => {
      setTrainStatuses(prev => {
        if (prev.get(trainId) === status) return prev;
        const updated = new Map(prev);
        updated.set(trainId, status);
        return updated;
      });
    };
  }, []);

  const filteredAndSortedTrains = useMemo(() => {
    const statusPriority: { [key: string]: number } = {
      'ACTION REQUIRED': 1,
      'MONITOR': 2,
      'GOOD': 3
    };

    let result = [...trainsDataWithSandBoxIds];


   result = trainsDataWithSandBoxIds.map(train => {
      const status = trainStatuses.get(train.trainId) || 'GOOD';
      return {
        ...train,
        status,
        statusPriority: statusPriority[status]
      };
    });

    
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      result = result.filter(train =>
        Object.values(train).some(val => val?.toString().toLowerCase().includes(searchLower))
      );
    }
    if (selectedFilters.length > 0) {
      const filterMapping: { [key: string]: string } = {
        'Status': 'status', 'Train ID': 'trainId', 'Fleet': 'fleet',
        'Start Location': 'startLocation', 'End Location': 'endLocation'
      };
      selectedFilters.forEach(({ filter, value }) => {
        const field = filterMapping[filter];
        if (field && value) {
          result = result.filter(train => String(train[field]) == value);
        }
      });
    }
    return result.map(train => ({
      ...train,
      status: trainStatuses.get(train.trainId) || 'GOOD',
      statusPriority: statusPriority[trainStatuses.get(train.trainId) || 'GOOD']
    })).sort((a, b) => a.statusPriority - b.statusPriority);

  }, [trainsDataWithSandBoxIds, trainStatuses, searchQuery, selectedFilters]);

  return (
    <Box sx={{ my: 2, width: '100%', overflowY: 'auto', maxHeight: '100vh', padding: '1rem' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <SandboxHeaderBar appProps={appProps} organizationProps={organizationProps} />
        </Grid>

        {/* {(
        <>
          <Grid container spacing={2} sx={{ mb: '1rem' }}>
            <SandUsageMap />
          </Grid>
        </>
      )} */}

        <Grid item xs={12} sm={6}>
          <SearchBar onSearch={handleSearch} />
         
        </Grid>

        <Grid item xs={12} sm={6}>
          <TrainFilters
            onFilterChange={handleFilterChange}
            selectedFilters={selectedFilters}
            selectedValues={selectedValues}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
        
          {selectedFilters.map((filter, i) => (
            <Chip
              key={i}
              label={`${filter.filter}: ${filter.value}`}
              onDelete={() => handleChipDelete(filter.filter)}
              color="primary"
            
            />
          ))}
        </Grid>

        <Grid item xs={12}>
          {trainListLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ padding: '1rem', overflowX: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Status', 'Sandboxes', 'Last Update', 'Name', 'Fleet', 'Train Id', 'Travelling From', 'Travelling To', '+100 Sandbox Pair Avg', '+200 Sandbox Pair Avg', 'Current Position', 'Action']
                      .map(header => (
                        <TableCell key={header}><TableSortLabel>{header}</TableSortLabel></TableCell>
                      ))}
                  </TableRow>
                </TableHead>

                {filteredAndSortedTrains.map(trainData => (
                  <SandboxTrainListTable
                    key={`train_${trainData.trainId}`}
                    selectedTrain={!!selectedTrainSandbox}
                    onSelectTrain={setSelectedTrainSandbox}
                    selectedTrainID={selectedTrainId}
                    setSelectedTrainID={setSelectedTrainID}
                    setOpenMap ={setOepnMap}
                    openMap = {openMap}
                    trainsData={trainData}
                    usageTrainID={usageTrainID}
                    setUsageTrainID={setUsageTrainID}
                    onMapShow={() => setIsMapShown(!isMapShown)}
                    onUpdateTrainStatus={handleUpdateTrainStatus}
                  />
                ))}
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SandboxTrainListContainer;
